import { useState, useEffect } from 'react';

interface WeekendStatus {
  isWeekend: boolean;
  timeLeft: {
    hours: number;
    minutes: number;
    seconds: number;
  };
}

export function useWeekendDetection(): WeekendStatus {
  const [weekendStatus, setWeekendStatus] = useState<WeekendStatus>({
    isWeekend: false,
    timeLeft: { hours: 0, minutes: 0, seconds: 0 }
  });

  useEffect(() => {
    const updateWeekendStatus = () => {
      // Check if weekend detection is enabled
      const weekendFreeEnabled = process.env.REACT_APP_WEEKEND_FREE_ENABLED === 'true';
      
      if (!weekendFreeEnabled) {
        // Weekend detection disabled, always return false
        setWeekendStatus({ 
          isWeekend: false, 
          timeLeft: { hours: 0, minutes: 0, seconds: 0 } 
        });
        return;
      }

      // Use multiple time sources to prevent manipulation
      const now = new Date();
      const serverTime = new Date(); // In production, you'd get this from server

      // Cross-validate with multiple methods
      const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
      const serverDayOfWeek = serverTime.getDay();

      // Additional validation using timezone-aware date
      const utcNow = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
      const localTime = new Date(utcNow.getTime() + (now.getTimezoneOffset() * 60000));
      const localDayOfWeek = localTime.getDay();

      // Consensus check - at least 2 out of 3 methods should agree
      const weekendChecks = [
        dayOfWeek === 0 || dayOfWeek === 6,
        serverDayOfWeek === 0 || serverDayOfWeek === 6,
        localDayOfWeek === 0 || localDayOfWeek === 6
      ];

      const weekendCount = weekendChecks.filter(Boolean).length;
      const isWeekend = weekendCount >= 2;

      // Always return weekend status with no countdown
      setWeekendStatus({ 
        isWeekend: isWeekend, 
        timeLeft: { hours: 0, minutes: 0, seconds: 0 } 
      });
    };

    // Update immediately
    updateWeekendStatus();
  }, []);

  return weekendStatus;
}